// Pure helpers for Step 3 (Preview): group raw rows by employee, fuzzy-match
// Employee/Customer/Class text against existing rosters, and validate per-employee
// percent totals. No React, no API calls — unit-testable in isolation, mirroring
// SmartImport/steps/step4Confirm.ts's style.
//
// Employee scoring is NOT reimplemented here — it's reused directly from
// SmartImport/steps/step3FuzzyMatch.ts (same 0-100 blended Levenshtein/token-overlap
// score, same thresholds) so both wizards stay in lockstep on what counts as a match.

import { v4 as uuid } from 'uuid';
import { scoreNames, bandFor } from '../SmartImport/steps/step3FuzzyMatch';
import { normalizeEmployeeName, parseNumber } from '../SmartImport/parsers';
import { TEMPLATE_GROUP_ID } from './wizardReducer';
import type {
	AllocationRowState,
	ColumnMapping,
	ColumnTarget,
	EmployeeGroup,
} from './wizardReducer';
import { isPercentageTotalValid } from 'utils/allocationCsv';

export interface RosterEmployee {
	id: string;
	fullName: string;
	active?: boolean;
	isActiveInApp?: boolean;
}

export interface TreeOption {
	title: string;
	value: string;
	children?: TreeOption[];
}

export interface FlatOption {
	title: string;
	value: string;
}

// Confident-match threshold for free-text Customer/Class resolution. Reuses
// step3FuzzyMatch's AUTO_ACCEPT band — anything below is left for manual review
// rather than guessed, since Customer/Class (unlike Employee) can't be created
// inline if wrong.
const CUSTOMER_CLASS_AUTO_ACCEPT = 85;

/** Flatten a QBO customer/class TreeSelect data array (any depth) into a flat list. */
export function flattenTreeOptions(options: TreeOption[] | undefined | null): FlatOption[] {
	const out: FlatOption[] = [];
	const walk = (nodes: TreeOption[] | undefined | null) => {
		(nodes ?? []).forEach((n) => {
			if (n.value !== undefined && n.value !== null && String(n.value) !== '' && n.title) {
				out.push({ title: String(n.title), value: String(n.value) });
			}
			if (n.children) walk(n.children);
		});
	};
	walk(options);
	return out;
}

export interface ResolvedOption {
	matched: boolean;
	value: string;
	label: string;
	score: number;
}

/** Fuzzy-match free text against a flat option list. Blank text is not "matched" — callers treat blank as the explicit blank-convention default instead. */
export function resolveCustomerClassText(text: string, options: FlatOption[]): ResolvedOption {
	const trimmed = text.trim();
	if (trimmed === '' || options.length === 0) {
		return { matched: false, value: '', label: '', score: 0 };
	}

	let bestScore = -1;
	let best: FlatOption | null = null;
	for (const opt of options) {
		const score = scoreNames(trimmed, opt.title);
		if (score > bestScore) {
			bestScore = score;
			best = opt;
		}
	}

	if (best && bestScore >= CUSTOMER_CLASS_AUTO_ACCEPT) {
		return { matched: true, value: best.value, label: best.title, score: bestScore };
	}
	return { matched: false, value: '', label: '', score: Math.max(bestScore, 0) };
}

/**
 * Group parsed rows by the mapped Employee column's raw text into one
 * EmployeeGroup per distinct name, each holding its Customer/Class/%Hours
 * sub-rows. Rows with a blank employee cell are dropped entirely — this
 * feature never creates a "blank name" placeholder row (unlike SmartImport).
 */
export function groupRowsByEmployee(
	rawRows: Array<Record<string, unknown>>,
	columnMappings: ColumnMapping[]
): EmployeeGroup[] {
	const employeeCol = columnMappings.find((m) => m.target === 'employee');
	if (!employeeCol) return [];

	const toRow = allocationRowReader(columnMappings);
	const groupsByKey = new Map<string, EmployeeGroup>();

	rawRows.forEach((row) => {
		const csvName = String(row[employeeCol.sourceHeader] ?? '').trim();
		if (csvName === '') return;

		const key = csvName.toLowerCase();
		let group = groupsByKey.get(key);
		if (!group) {
			group = {
				groupId: uuid(),
				csvName,
				matchStatus: 'review',
				matchedEmployeeId: null,
				matchedEmployeeName: null,
				matchScore: null,
				rows: [],
			};
			groupsByKey.set(key, group);
		}

		group.rows.push(toRow(row));
	});

	return Array.from(groupsByKey.values());
}

/**
 * Returns a reader turning one raw file row into an unresolved allocation row
 * via the mapped Customer/Class/% columns. With no % column the percent is null
 * (to be entered or split equally in Step 3).
 */
function allocationRowReader(columnMappings: ColumnMapping[]) {
	const percentCol = columnMappings.find((m) => m.target === 'percent');
	const customerCol = columnMappings.find((m) => m.target === 'customer');
	const classCol = columnMappings.find((m) => m.target === 'class');

	return (row: Record<string, unknown>): AllocationRowState => {
		const customerText = customerCol ? String(row[customerCol.sourceHeader] ?? '').trim() : '';
		const classText = classCol ? String(row[classCol.sourceHeader] ?? '').trim() : '';
		const parsedPercent = percentCol ? parseNumber(row[percentCol.sourceHeader]) : NaN;
		return {
			rowId: uuid(),
			customerText,
			classText,
			customerId: '',
			customerName: customerText === '' ? '-' : '',
			classId: '',
			className: classText === '' ? '-' : '',
			customerReview: customerText !== '',
			classReview: classText !== '',
			percent: Number.isNaN(parsedPercent) ? null : parsedPercent,
		};
	};
}

// ---------------------------------------------------------------------------
// Template mode: a file with Customer/Class lines but no Employee column is one
// allocation, applied to the employees the user selects in Step 3.
// ---------------------------------------------------------------------------

/** True when no column is mapped to Employee, i.e. the file is one shared allocation. */
export const isTemplateMode = (columnMappings: ColumnMapping[]) =>
	!columnMappings.some((m) => m.target === 'employee');

/**
 * Why Step 2 can't continue with these mappings, or null if it can. An Employee
 * column needs Customer/Class or % beside it; without Employee (template mode)
 * there must be Customer or Class lines to apply. % is optional in both.
 */
export function mappingProblem(columnMappings: ColumnMapping[]): string | null {
	const has = (t: ColumnTarget) => columnMappings.some((m) => m.target === t);
	const hasLines = has('customer') || has('class');
	if (has('employee')) {
		return hasLines || has('percent')
			? null
			: 'Map at least one column to Customer, Class or % of Hours to continue.';
	}
	return hasLines
		? null
		: 'Map at least one column to Employee, Customer or Class to continue.';
}

/** Build the template's resolved allocation lines. Rows blank in every mapped column are skipped. */
export function buildTemplateGroup(
	rawRows: Array<Record<string, unknown>>,
	columnMappings: ColumnMapping[],
	customerOptions: TreeOption[],
	classOptions: TreeOption[]
): EmployeeGroup {
	const toRow = allocationRowReader(columnMappings);
	const hasPercentCol = columnMappings.some((m) => m.target === 'percent');
	// With a % column, a 0% line allocates nothing — drop it rather than make
	// the user scroll past (and save) dozens of them. Unparseable % (null) stays
	// so it can be fixed. Without a % column, keep every line that names something.
	const rows = rawRows
		.map(toRow)
		.filter((r) =>
			hasPercentCol ? r.percent !== 0 : r.customerText !== '' || r.classText !== ''
		);
	return {
		groupId: TEMPLATE_GROUP_ID,
		csvName: '',
		matchStatus: 'matched',
		matchedEmployeeId: null,
		matchedEmployeeName: null,
		matchScore: null,
		rows: resolveRowsCustomerClass(
			rows,
			flattenTreeOptions(customerOptions),
			flattenTreeOptions(classOptions)
		),
	};
}

/** `count` two-decimal percentages summing to exactly 100 (3 -> 33.34, 33.33, 33.33). */
export function splitEqually(count: number): number[] {
	if (count <= 0) return [];
	const base = Math.floor(10000 / count);
	const remainder = 10000 - base * count;
	return Array.from({ length: count }, (_, i) => (base + (i < remainder ? 1 : 0)) / 100);
}

/** Employees the template can be applied to: active in QuickBooks and in CAP. */
export const selectableEmployees = (roster: RosterEmployee[]) =>
	roster.filter((e) => e.active !== false && e.isActiveInApp !== false);

/** One matched group per selected employee, each carrying the template's lines. */
export function expandTemplate(
	template: EmployeeGroup,
	employees: RosterEmployee[]
): EmployeeGroup[] {
	return employees.map((e) => ({
		...template,
		groupId: `template-${e.id}`,
		csvName: e.fullName,
		matchStatus: 'matched',
		matchedEmployeeId: e.id,
		matchedEmployeeName: e.fullName,
	}));
}

/** The employees the template will be applied to, per the user's explicit choice (none until they choose). */
export function templateTargetEmployees(
	applyTo: 'all' | 'selected' | null,
	activeEmployees: RosterEmployee[],
	selectedIds: string[]
): RosterEmployee[] {
	if (applyTo === 'all') return activeEmployees;
	if (applyTo === 'selected') return activeEmployees.filter((e) => selectedIds.includes(e.id));
	return [];
}

/** What still blocks a template import, as short user-facing sentences (empty = ready). */
export function templateIssues(
	template: EmployeeGroup | null,
	applyTo: 'all' | 'selected' | null,
	targetCount: number
): string[] {
	const issues: string[] = [];
	if (applyTo === null) issues.push('Choose who this allocation applies to.');
	else if (targetCount === 0)
		issues.push(applyTo === 'all' ? 'There are no active employees to apply it to.' : 'Select at least one employee.');
	if (!template || template.rows.length === 0) {
		issues.push('Your file has no allocation lines to apply.');
		return issues;
	}
	if (template.rows.some((r) => r.percent === null)) {
		issues.push('Enter a % of Hours for every line (or use Split equally).');
	} else {
		const { isValid, total } = validatePercentSums([template])[0];
		if (!isValid) issues.push(`The lines total ${total}% — they must total 100%.`);
	}
	const review = template.rows.filter((r) => r.customerReview || r.classReview).length;
	if (review > 0) issues.push(`${review} line(s) need a Customer/Class picked.`);
	return issues;
}

/** Template is importable: valid lines (same rules as an employee group) and at least one employee picked. */
export const canImportTemplate = (template: EmployeeGroup | null, employeeIds: string[]) =>
	!!template &&
	template.rows.length > 0 &&
	employeeIds.length > 0 &&
	canImport([{ ...template, matchedEmployeeId: TEMPLATE_GROUP_ID }]);

/** Resolve every group's employee against the roster. Only 'matched'/'review' — no 'new' band, per the approved plan (no inline employee creation). */
export function resolveEmployeeGroups(
	groups: EmployeeGroup[],
	roster: RosterEmployee[]
): EmployeeGroup[] {
	return groups.map((g) => {
		if (roster.length === 0) {
			return { ...g, matchStatus: 'review', matchedEmployeeId: null, matchedEmployeeName: null, matchScore: null };
		}

		const normalized = normalizeEmployeeName(g.csvName);
		let bestScore = -1;
		let bestEmp: RosterEmployee | null = null;
		for (const emp of roster) {
			const score = scoreNames(normalized, normalizeEmployeeName(emp.fullName));
			if (score > bestScore) {
				bestScore = score;
				bestEmp = emp;
			}
		}

		const isMatched = bestEmp !== null && bandFor(bestScore) === 'matched';
		return {
			...g,
			matchStatus: isMatched ? 'matched' : 'review',
			matchedEmployeeId: isMatched ? bestEmp!.id : null,
			matchedEmployeeName: isMatched ? bestEmp!.fullName : null,
			matchScore: bestScore >= 0 ? bestScore : null,
		};
	});
}

/**
 * Resolve each row's Customer/Class free text against the live option lists.
 * Blank text stays the Blank Customer/Class default. Text that matches nothing
 * also defaults to Blank, flagged *Unmatched so the preview can say so — it no
 * longer blocks Import. Only when the option list itself is unavailable (QBO
 * fetch failed) is the row left for manual review, since then nothing could be
 * checked at all.
 */
function resolveRowsCustomerClass(
	rows: AllocationRowState[],
	customerOptions: FlatOption[],
	classOptions: FlatOption[]
): AllocationRowState[] {
	return rows.map((r) => {
		let next = r;
		if (r.customerText !== '' && customerOptions.length > 0) {
			const resolved = resolveCustomerClassText(r.customerText, customerOptions);
			next = resolved.matched
				? { ...next, customerId: resolved.value, customerName: resolved.label, customerReview: false }
				: { ...next, customerId: '', customerName: '-', customerReview: false, customerUnmatched: true };
		}
		if (r.classText !== '' && classOptions.length > 0) {
			const resolved = resolveCustomerClassText(r.classText, classOptions);
			next = resolved.matched
				? { ...next, classId: resolved.value, className: resolved.label, classReview: false }
				: { ...next, classId: '', className: '-', classReview: false, classUnmatched: true };
		}
		return next;
	});
}

/** Full Step 3 init pipeline: group -> resolve customer/class -> resolve employee. */
export function buildEmployeeGroups(
	rawRows: Array<Record<string, unknown>>,
	columnMappings: ColumnMapping[],
	employeeRoster: RosterEmployee[],
	customerOptions: TreeOption[],
	classOptions: TreeOption[]
): EmployeeGroup[] {
	const flatCustomers = flattenTreeOptions(customerOptions);
	const flatClasses = flattenTreeOptions(classOptions);

	const grouped = groupRowsByEmployee(rawRows, columnMappings).map((g) => ({
		...g,
		rows: resolveRowsCustomerClass(g.rows, flatCustomers, flatClasses),
	}));

	return resolveEmployeeGroups(grouped, employeeRoster);
}

export interface GroupValidation {
	groupId: string;
	total: number;
	isValid: boolean;
	/** Valid only thanks to the ±0.01 tolerance (e.g. 99.99); saved as exactly 100. */
	isRoundedTo100: boolean;
}

/**
 * Per-employee-group percent-sum validation. Within ±0.01 of 100 is valid —
 * 33.33 x 3 = 99.99 is rounding, not a user error, and the backend saves those
 * rows at exactly 100 (CAP-115). Same rule as the backend's bulk import.
 */
export function validatePercentSums(groups: EmployeeGroup[]): GroupValidation[] {
	return groups.map((g) => {
		const rawTotal = g.rows.reduce((sum, r) => sum + (r.percent ?? 0), 0);
		const total = Number(rawTotal.toFixed(2));
		const isValid = isPercentageTotalValid(rawTotal);
		return { groupId: g.groupId, total, isValid, isRoundedTo100: isValid && total !== 100 };
	});
}

/** Display name for a group: the matched roster name, else the name from the file. */
export const groupDisplayName = (group: EmployeeGroup) =>
	group.matchedEmployeeName ?? group.csvName;

/** The group a backend error refers to (errors are keyed by matched employee id). */
export const findGroupByEmployeeId = (groups: EmployeeGroup[], employeeId?: string) =>
	employeeId ? groups.find((g) => g.matchedEmployeeId === employeeId) : undefined;

/** True when every row has a parseable percent, every group is a matched employee with no pending Customer/Class review, and every group's percent total is valid. */
export function canImport(groups: EmployeeGroup[]): boolean {
	if (groups.length === 0) return false;
	const validations = new Map(validatePercentSums(groups).map((v) => [v.groupId, v]));
	return groups.every((g) => {
		if (g.matchStatus !== 'matched' || !g.matchedEmployeeId) return false;
		if (!validations.get(g.groupId)?.isValid) return false;
		return g.rows.every(
			(r) => r.percent !== null && !r.customerReview && !r.classReview
		);
	});
}

// ---------------------------------------------------------------------------
// Import payload assembly + 422 error parsing — mirrors the shape/convention of
// SmartImport/steps/step4Confirm.ts's buildConfirmPayload/parseConfirmErrors,
// against the CAP-Backend POST /employee-config/bulk-import contract.
// ---------------------------------------------------------------------------

export interface DirectAllocationEntry {
	allocation: number;
	classId: string;
	className: string;
	customerId: string;
	customerName: string;
}

export interface BulkDirectAllocationRow {
	employeeId: string;
	isActive: boolean;
	directAllocation: DirectAllocationEntry[];
}

export interface BulkEmployeeConfigInterface {
	companyId: string;
	payPeriodId: string;
	rows: BulkDirectAllocationRow[];
}

export function buildImportPayload(
	groups: EmployeeGroup[],
	ctx: { companyId: string; payPeriodId: string }
): BulkEmployeeConfigInterface {
	const rows: BulkDirectAllocationRow[] = groups
		.filter((g) => g.matchStatus === 'matched' && g.matchedEmployeeId)
		.map((g) => ({
			employeeId: g.matchedEmployeeId as string,
			isActive: true,
			directAllocation: g.rows.map((r) => ({
				// Full precision: rounding each row here is what produced 99.99% totals.
				// The backend rounds the employee's rows together so they sum to 100.
				allocation: r.percent ?? 0,
				classId: r.classId || '',
				className: r.className || '-',
				customerId: r.customerId || '',
				customerName: r.customerName || '-',
			})),
		}));

	return { companyId: ctx.companyId, payPeriodId: ctx.payPeriodId, rows };
}

export interface PreValidationError {
	code: string;
	message: string;
	employeeId?: string;
	context?: unknown;
}

/** Extract PreValidationError[] from a 422 axios error — backend nests the structured payload as a JSON string under response.data.message. */
export function parseBulkImportErrors(err: unknown): PreValidationError[] {
	const raw = (err as { response?: { data?: { message?: unknown } } })?.response?.data
		?.message;
	if (typeof raw !== 'string') return [];
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed?.errors) ? parsed.errors : [];
	} catch {
		return [];
	}
}
